import http from './http';
import { ICustomerDocument, IFilter } from '../resource';
import { toDocumentFileName } from '../utils/documentFileName';

export interface DocumentQueryParams {
  page?: number;
  limit?: number;
  archived?: number;
  [key: string]: any;
}

/** Last write wins when the same field key appears more than once. */
function dedupeCustomizePayload(payload: { key: string; value: any }[]) {
  const map = new Map<string, any>();
  for (const item of payload) {
    map.set(item.key, item.value);
  }
  return Array.from(map, ([key, value]) => ({ key, value }));
}

export const getDocuments = async (customerId: string | undefined, params?: DocumentQueryParams, signal?: AbortSignal) => {
  return await http.get(`/docs/${customerId}/documents`, params, {}, signal);
};

export const getAllDocuments = async (id: string, params?: DocumentQueryParams, signal?: AbortSignal) => {
  return await http.get('/docs', params, {}, signal);
};

export const getDocument = async (customerId: string | undefined, id: string) => {
  return await http.get(`/docs/${customerId}/documents/${id}`);
};

export const generateDocument = async (customerId: string | undefined, templateId?: string, automationConfig?: { isAutomated?: boolean; automationFrequency?: 'daily' | 'weekly' | 'monthly' | 'yearly' }) => {
  return await http.post(`/docs/${customerId}/documents`, { templateId, automationConfig });
};

export const downloadPdf = async (url: string): Promise<Blob> => {
  const response = await http.publicGet(url, { responseType: 'blob' });
  return response.data as Blob;
};

export const removeDocument = async (customerId: string | undefined, id: string) => {
  return await http.put(`/docs/${customerId}/documents/${id}/archive`);
};

export const removeForeverDocument = async (customerId: string | undefined, id: string, password?: string) => {
  return await http.delete(`/docs/${customerId}/documents/${id}`, password ? { password } : undefined);
};

export const restoreDocument = async (customerId: string | undefined, id: string) => {
  return await http.put(`/docs/${customerId}/documents/${id}/restore`);
};

export const duplicateDocument = async (customerId: string, id: string) => {
  return await http.post(`/docs/${customerId}/documents/${id}/duplicate`);
};

export const createDocumentBackup = async (customerId: string, id: string) => {
  return await http.post(`/docs/${customerId}/documents/${id}/backup`);
};

export const restoreBackupAsMaster = async (
  customerId: string,
  backupId: string,
  masterUpdatedAt: string
) => {
  return await http.post(`/docs/${customerId}/documents/${backupId}/restore-as-master`, {
    masterUpdatedAt,
  });
};

export const generateCustomDocument = async (layout: any) => {
  return await http.post(`/docs/customize`, layout);
};

export const getDocumentFields = async (id: string) => {
  return await http.get(`/docs/${id}/fields`);
};

export const customizeDocument = async (
  customerId: string | undefined,
  id: string,
  payload: { key: string; value: any }[],
  expectedUpdatedAt?: string
) => {
  const dedupedPayload = dedupeCustomizePayload(payload);
  const contacts = dedupedPayload.filter((item) => item.key === 'contact_fields');

  if (contacts.length) {
    for (const contact of contacts) {
      if (contact.value.image) {
        const result = await uploadContactImage(contact.value.image);
        dedupedPayload.forEach((item) => {
          if (item.key === 'contact_fields' && item.value._id === contact.value._id) {
            item.value.image = result.data;
          }
        });
      }
    }
  }

  // Optimistic lock: server rejects with 409 if fields changed since load/last save.
  if (expectedUpdatedAt) {
    dedupedPayload.push({ key: 'expectedUpdatedAt', value: expectedUpdatedAt });
  }

  return await http.put(`/docs/${customerId}/documents/${id}`, dedupedPayload);
};

export const previewDocument = async (
  customerId: string | undefined,
  id: string,
  payload: { key: string; value: any }[]
) => {
  const dedupedPayload = dedupeCustomizePayload(payload);
  const contacts = dedupedPayload.filter((item) => item.key === 'contact_fields');

  if (contacts.length) {
    for (const contact of contacts) {
      if (contact.value.image) {
        const result = await uploadContactImage(contact.value.image);
        dedupedPayload.forEach((item) => {
          if (item.key === 'contact_fields' && item.value._id === contact.value._id) {
            item.value.image = result.data;
          }
        });
      }
    }
  }

  return await http.put(`/docs/${customerId}/documents/${id}/preview`, dedupedPayload);
};

export const customizeDocumentContacts = async (
  customerId: string | undefined,
  id: string,
  payload: any
) => {
  const form: any = new FormData();
  form.append('name', payload.name);
  form.append('image', payload.image);
  form.append('role', payload.role);
  form.append('second_role', payload.second_role);
  form.append('email', payload.email);
  form.append('tel', payload.tel);
  form.append('id', payload._id);
  form.append('link', payload.link);
  return await http.put(`/docs/${customerId}/documents/${id}/contact`, form);
};

export const createCustomerDocument = async (customerId: string, document: Partial<ICustomerDocument>) => {
  return await http.post(`/docs/${customerId}/customer-docs`, document);
};

export const getCustomerDocument = async (customerId: string, docId: string) => {
  return await http.get(`/docs/${customerId}/customer-docs/${docId}`);
};

export const getCustomerDocuments = async (customerId: string, filter?: IFilter & DocumentQueryParams, signal?: AbortSignal) => {
  return await http.get(`/docs/${customerId}/customer-docs`, filter, {}, signal);
};

export const getAllCustomerDocuments = async (customerId: string, filter?: IFilter & DocumentQueryParams, signal?: AbortSignal) => {
  return await http.get('/docs/customer-docs', filter, {}, signal);
};

export const publishCustomerDocuments = async (customerId: string, url: string, customerDocIds: string[]) => {
  return await http.post(`/docs/${customerId}/customer-docs/publish`, { url, customerDocIds });
};

export const updateCustomerDocument = async (
  customerId: string,
  docId: string,
  document: Partial<ICustomerDocument>
) => {
  return await http.put(`/docs/${customerId}/customer-docs/${docId}`, document);
};

export const removeCustomerDocuments = async (customerId: string, ids: string[]) => {
  return await http.delete(`/docs/${customerId}/customer-docs`, { ids });
};

export const removeForeverCustomerDocument = async (customerId: string, id: string) => {
  return await http.delete(`/docs/${customerId}/customer-docs/${id}`);
};

export const recoverCustomerDocument = async (customerId: string, id: string) => {
  return await http.put(`/docs/${customerId}/customer-docs/${id}/recover`);
};

export const uploadLogoImage = async (file) => {
  const form: any = new FormData();
  form.append('avatar', file);
  return await http.post('/docs/logo-upload', form);
};


export const uploadContactImage = async (image) => {
  const form: any = new FormData();
  form.append('image', image);
  return await http.post('/docs/contact-upload', form);
};

// Upload a react-pdf generated PDF file along with metadata
export const uploadGeneratedPdf = async (
  customerId: string | undefined,
  id: string,
  file: Blob,
  metadata: any = {}
) => {
  const form: any = new FormData();
  form.append('file', file, metadata?.file_name ? toDocumentFileName(metadata.file_name) : `${metadata?.title || 'factsheet'}.pdf`);
  form.append('metadata', JSON.stringify(metadata));
  return await http.post(`/docs/${customerId}/documents/${id}/upload-pdf`, form);
};

export const getPrFactsheetPreview = async (documentId: string, params?: { until?: string }) => {
  return await http.get(`/docs/${documentId}/pr-preview`, params);
};

/** Force-generate automated factsheet PDF on the server (S3 upload + published_at update) */
export const forceGenerateFactsheet = async (
  customerId: string | undefined,
  documentId: string,
  options?: { targetDate?: string }
) => {
  const body = options?.targetDate ? { targetDate: options.targetDate } : {};
  return await http.post(`/docs/${customerId}/documents/${documentId}/force-generate`, body);
};